import type { PermissionsMap, Recurso } from "@printflow/auth-shared";
import { canCodigo as canCodigoShared, can as canShared } from "@printflow/auth-shared";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { ApiError, fetchMe, type Me } from "./api";

interface SessionState {
  me: Me | null;
  cargando: boolean;
  /**
   * `true` si el usuario tiene el permiso. Es EXACTAMENTE la misma funcion que
   * usa el worker para autorizar (`canPrintflowDo` de `@printflow/auth-shared`),
   * asi que la UI no puede divergir de lo que la API permite.
   *
   * Decide que MOSTRAR, no que PERMITIR: cada endpoint del worker aplica su
   * propio `requirePermission`. Un permiso cuyo unico control sea este es un bug.
   */
  can: (recurso: Recurso, accion: string) => boolean;
  /** Igual que `can` pero desde el codigo plano (`"presupuestos.aprobar"`). */
  canCodigo: (codigo: string) => boolean;
  recargar: () => Promise<void>;
}

/**
 * Si un error de `/me` significa «no hay sesion usable» o si es un fallo real.
 *
 * **401** es sin sesion y **403** es usuario desactivado: los dos son estados
 * normales y la app muestra el login. Cualquier otra cosa —un 500, la red
 * caida, el worker abajo— **no** lo es.
 *
 * La distincion importa porque en los dos casos la pantalla termina siendo la
 * misma: sin `me`, `App` renderiza el formulario de login. Si un 500 se tragara
 * como «sin sesion», alguien con la sesion perfectamente viva veria el login,
 * escribiria sus credenciales y volveria a fallar sin que nada explique por que
 * — y en la consola no quedaria rastro.
 *
 * Va aparte del componente para testearlo en `node`, sin montar el provider.
 * Mismo criterio que `minutosDesde` en `inactividad.ts`.
 */
export function esSinSesion(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

const SIN_PERMISOS: PermissionsMap = {};

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(async () => {
    setCargando(true);
    try {
      setMe(await fetchMe());
    } catch (err) {
      if (!esSinSesion(err)) console.error("[session] fallo al resolver la sesion:", err);
      setMe(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const can = useCallback(
    (recurso: Recurso, accion: string) => canShared(me?.permisos ?? SIN_PERMISOS, recurso, accion),
    [me],
  );

  const canCodigo = useCallback(
    (codigo: string) => canCodigoShared(me?.permisos ?? SIN_PERMISOS, codigo),
    [me],
  );

  return (
    <SessionContext.Provider value={{ me, cargando, can, canCodigo, recargar }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession debe usarse dentro de <SessionProvider>");
  return ctx;
}
